import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.payments_api.payment_discounts;

// TypeScript interfaces for payment discounts
export enum DiscountScopeType {
  SINGLE_ITEM = 'item',
  SINGLE_CATEGORY = 'category',
  SINGLE_MENU = 'menu',
  GLOBAL = 'global',
  MULTI_ITEM = 'multi_item',
  MULTI_CATEGORY = 'multi_category'
}

// Time-based scheduling types
export interface TimeFrame {
  id?: string;
  name: string;
  days: number[]; // 1=Monday through 7=Sunday
  start_time: string; // HH:MM format (24-hour)
  end_time: string; // HH:MM format (24-hour)
  active?: boolean;
}

export interface TimeframesConfig {
  frames: TimeFrame[];
}

export interface ScheduleInfo {
  current_status: 'active' | 'inactive' | 'error' | 'always_active';
  status_message: string;
  timezone: string;
  next_change?: string; // ISO string
}

export interface PaymentDiscount {
  payment_discount_id: number;
  payment_discount_name: string;
  creation_date?: string;
  expiration_date?: string;
  isvalid: boolean;
  current_number_used: number;
  current_amount_used: number;
  max_number?: number;
  max_budget?: number;
  local_id: number;
  payment_discount_amount?: number;
  payment_discount_percentage?: number;
  // Legacy single scope fields
  isglobal?: boolean;
  menu_id?: number;
  menu_category_id?: number;
  item_id?: number;
  // New enhanced fields
  scope_type: DiscountScopeType;
  item_ids?: number[];
  category_ids?: number[];
  // Time-based scheduling
  always_active?: boolean;
  schedule_info?: ScheduleInfo;
  // Manual application
  is_manual?: boolean;
}

export interface CreateDiscountPayload {
  payment_discount_name: string;
  local_id: number;
  payment_discount_amount?: number;
  payment_discount_percentage?: number;
  max_number?: number;
  max_budget?: number;
  expiration_date?: string;
  // Single scope fields (legacy - mutually exclusive with multi-scope)
  isglobal?: boolean;
  menu_id?: number;
  menu_category_id?: number;
  item_id?: number;
  // Multi-scope fields (mutually exclusive with single scope)
  item_ids?: number[];
  category_ids?: number[];
  // Time-based scheduling
  always_active: boolean;
  timeframes_config?: TimeframesConfig;
  // Manual application
  is_manual?: boolean;
}

export interface UpdateDiscountPayload {
  payment_discount_name?: string;
  payment_discount_amount?: number;
  payment_discount_percentage?: number;
  max_number?: number;
  max_budget?: number;
  expiration_date?: string;
  isvalid?: boolean;
  // Scope fields (when changing scope, all scope fields must be provided)
  isglobal?: boolean;
  menu_id?: number;
  menu_category_id?: number;
  item_id?: number;
  item_ids?: number[];
  category_ids?: number[];
  // Time-based scheduling
  always_active?: boolean;
  timeframes_config?: TimeframesConfig;
  // Manual application
  is_manual?: boolean;
}


// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Wrap with cache
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'payments_api',
  group: 'payment_discounts',
  defaultTtl: '1m',
  hashLargeSegments: true,
  emitMeta: true,
});


export const getAllPaymentDiscounts = async () => {
    return await retryWithBackoff(() =>
        cachedApi.get('/GetAllPaymentDiscounts', undefined, { ttl: '2m' })
    );
}

export const getPaymentDiscountById = async (paymentDiscountId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountById/${paymentDiscountId}`, undefined, { ttl: '2m' })
    );
}

export const getPaymentDiscountsByLocalId = async (localId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountsByLocalId/${localId}`, undefined, { ttl: '2m' })
    );
}

export const getPaymentDiscountsByMenuId = async (menuId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountsByMenuId/${menuId}`, undefined, { ttl: '2m' })
    );
}

export const getPaymentDiscountsByItemId = async (itemId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountsByItemId/${itemId}`, undefined, { ttl: '2m' })
    );
}

export const getPaymentDiscountsByCategoryId = async (menuCategoryId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountsByCategoryId/${menuCategoryId}`, undefined, { ttl: '2m' })
    );
}

export const updatePaymentDiscountById = async (paymentDiscountId: number, paymentDiscountData: any) => {
    const res = await retryWithBackoff(() =>
        api.put(`/UpdatePaymentDiscountById/${paymentDiscountId}`, paymentDiscountData)
    );
    await cachedApi.clear();
    return res;
}

export const changePaymentDiscountStatusById = async (paymentDiscountId: number) => {
    const res = await retryWithBackoff(() =>
        api.put(`/ChangePaymentDiscountStatusById/${paymentDiscountId}`)
    );
    await cachedApi.clear();
    return res;
}

export const createPaymentDiscount = async (paymentDiscountData: any) => {
    const res = await retryWithBackoff(() =>
        api.post('/CreatePaymentDiscount', paymentDiscountData)
    );
    await cachedApi.clear();
    return res;
}

export const deletePaymentDiscount = async (paymentDiscountId: number) => {
    const res = await retryWithBackoff(() =>
        api.delete(`/DeletePaymentDiscount/${paymentDiscountId}`)
    );
    await cachedApi.clear();
    return res;
}

// Get discounts by scope type
export const getPaymentDiscountsByScopeType = async (scopeType: string) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentDiscountsByScopeType/${scopeType}`, undefined, { ttl: '2m' })
    );
}

// Get applicable discounts for multiple items
export const getPaymentDiscountsByMultipleItems = async (itemIds: number[]) => {
    return await deduplicateRequest(
        `getPaymentDiscountsByMultipleItems:${itemIds.sort().join(',')}`,
        () => retryWithBackoff(() =>
            api.post('/GetPaymentDiscountsByMultipleItems', { item_ids: itemIds })
        )
    );
}

// post GetDiscountApplicationInfo/local_id with payload {pedidos_ids: [1,2,3..]}
export const getDiscountApplicationInfo = async (localId: number, pedidosIds: number[]) => {
    return await deduplicateRequest(
        `getDiscountApplicationInfo:${localId}:${pedidosIds.sort().join(',')}`,
        () => retryWithBackoff(() =>
            api.post(`/GetDiscountApplicationInfo/${localId}`, { "pedidos_ids": pedidosIds })
        )
    );
}

// Time-based scheduling endpoints
export const getDiscountTimeframesById = async (discountId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetDiscountTimeframesById/${discountId}`, undefined, { ttl: '2m' })
    );
}

/**
 * @ns.route('/GetManualDiscountsByLocalId/<int:local_id>')
@ns.doc(security='bearerAuth')
class GetManualDiscountsByLocalId(Resource):
    @ns.doc('get_manual_discounts_by_local')
    @jwt_required()
    def get(self, local_id):
        '''Get all manual discounts by local (discounts that require manual application)'''
        import logging
        logger = logging.getLogger(__name__)
        logger.info(f"Getting manual discounts for local_id: {local_id}")
        
        # Query only manual discounts (is_manual=True) that are valid
        discounts = PaymentDiscount.query.filter_by(
            local_id=local_id,
            is_manual=True,
            isvalid=True
        ).all()
        logger.info(f"Found {len(discounts)} manual discounts for local_id {local_id}")
        
        result = []
        for discount in discounts:
            discount_helper = PaymentDiscountHelper(payment_discount_id=discount.payment_discount_id)
            discount_dict = discount_helper.toDict()
            result.append(discount_dict)
        
        logger.info(f"Returning {len(result)} manual discount dictionaries")
        return result
 */

export const getManualDiscountsByLocalId = async (localId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetManualDiscountsByLocalId/${localId}`, undefined, { ttl: '5m' })
    );
}

export const batchCheckDiscountStatus = async (discountIds: number[]) => {
    return await deduplicateRequest(
        `batchCheckDiscountStatus:${discountIds.sort().join(',')}`,
        () => retryWithBackoff(() =>
            api.post('/BatchCheckDiscountStatus', { discount_ids: discountIds })
        )
    );
}

    


